import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { Lock, Check, Circle, Loader2, AlertTriangle, Clock, KeyRound, XCircle, CheckCircle, Trash2, X, User, Shield, Save, Undo2, Bell, Home, Crown, Sparkles, BadgeInfo, Phone, FileText, Key, ClipboardList } from 'lucide-react';

const Settings = () => {
  const { user, refreshUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('profile');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deletionRequest, setDeletionRequest] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(true);

  const [profileForm, setProfileForm] = useState({
    nom: user?.nom || '',
    email: user?.email || '',
    bio: user?.bio || '',
    telephone: user?.telephone || ''
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Charger la demande de suppression existante
  useEffect(() => {
    const fetchDeletionRequest = async () => {
      try {
        const res = await api.get('/account/deletion-request');
        setDeletionRequest(res.data.request);
      } catch (err) {
        // Pas de demande en cours
        setDeletionRequest(null);
      } finally {
        setLoadingRequest(false);
      }
    };
    fetchDeletionRequest();
  }, []);

  const handleProfileChange = (e) => {
    setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/users/me', profileForm);
      setMessage({ type: 'success', text: 'Profil mis à jour avec succès !' });
      if (refreshUser) refreshUser();
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      setLoading(false);
      return;
    }

    if (passwordForm.new_password.length < 6) {
      setMessage({ type: 'error', text: 'Le mot de passe doit contenir au moins 6 caractères' });
      setLoading(false);
      return;
    }

    try {
      await api.put('/users/me/password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      setMessage({ type: 'success', text: 'Mot de passe mis à jour avec succès !' });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Mot de passe actuel incorrect' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteReason.trim()) {
      setMessage({ type: 'error', text: 'Veuillez indiquer une raison pour votre demande' });
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/account/deletion-request', { raison: deleteReason });
      setDeletionRequest(res.data.request || res.data);
      setShowDeleteModal(false);
      setDeleteReason('');
      setMessage({ type: 'success', text: 'Votre demande de suppression a été envoyée. Elle sera traitée par un administrateur.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Erreur lors de l\'envoi de la demande' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeletionRequest = async () => {
    setLoading(true);
    try {
      await api.delete('/account/deletion-request');
      setDeletionRequest(null);
      setMessage({ type: 'success', text: 'Votre demande de suppression a été annulée' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de l\'annulation de la demande' });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profil', icon: <User size={18} /> },
    { id: 'security', label: 'Sécurité', icon: <Shield size={18} /> },
    { id: 'danger', label: 'Compte', icon: <AlertTriangle size={18} /> }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Link 
              to="/profile"
              className="p-2 bg-white rounded-xl shadow-sm hover:shadow-md transition text-gray-500 hover:text-indigo-600"
            >
              ← Retour
            </Link>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Paramètres
              </h1>
              <p className="text-gray-500 mt-1">Gérez votre compte et vos préférences</p>
            </div>
          </div>
        </div>

        {/* User Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
              {user?.nom?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-800">{user?.nom}</h2>
              <p className="text-gray-500">{user?.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  user?.role === 'admin' ? 'bg-red-100 text-red-600' :
                  user?.role === 'author' ? 'bg-green-100 text-green-600' :
                  'bg-blue-100 text-blue-600'
                }`}>
                  {user?.role === 'admin' ? <><Crown className="w-3 h-3 inline" /> Admin</> : 
                   user?.role === 'author' ? <><Sparkles className="w-3 h-3 inline" /> Auteur</> : <><User className="w-3 h-3 inline" /> Utilisateur</>}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Message Alert */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-700' 
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.type === 'success' ? <CheckCircle size={20} /> : <XCircle size={20} />}
            <span className="font-medium">{message.text}</span>
            <button 
              onClick={() => setMessage({ type: '', text: '' })}
              className="ml-auto text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          <div className="flex border-b border-gray-100">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-indigo-50 text-indigo-600 border-b-2 border-indigo-600'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                }`}
              >
                <span className="text-xl">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="p-6 md:p-8">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <User className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Informations personnelles</h2>
                    <p className="text-sm text-gray-500">Mettez à jour vos informations de profil</p>
                  </div>
                </div>
                
                <form onSubmit={handleProfileSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <BadgeInfo className="w-4 h-4 text-indigo-500" /> Nom complet
                        </span>
                      </label>
                      <input
                        type="text"
                        name="nom"
                        value={profileForm.nom}
                        onChange={handleProfileChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                        placeholder="Votre nom"
                      />
                    </div>
                    
                    
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <span className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-indigo-500" /> Téléphone
                      </span>
                    </label>
                    <input
                      type="tel"
                      name="telephone"
                      value={profileForm.telephone}
                      onChange={handleProfileChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                      placeholder="+225 6XX XXX XXX"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-indigo-500" /> Bio
                      </span>
                    </label>
                    <textarea
                      name="bio"
                      value={profileForm.bio}
                      onChange={handleProfileChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition resize-none"
                      placeholder="Parlez un peu de vous..."
                    />
                    <p className="text-xs text-gray-400 mt-1">{profileForm.bio?.length || 0}/500 caractères</p>
                  </div>
                  
                  <div className="flex justify-end pt-4 border-t border-gray-100">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          <span>Enregistrement...</span>
                        </>
                      ) : (
                        <>
                          <Save size={18} />
                          <span>Enregistrer les modifications</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Shield size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Sécurité du compte</h2>
                    <p className="text-sm text-gray-500">Modifiez votre mot de passe</p>
                  </div>
                </div>
                
                <form onSubmit={handlePasswordSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <span className="flex items-center gap-2">
                        <Key className="w-4 h-4 text-amber-500" /> Mot de passe actuel
                      </span>
                    </label>
                    <input
                      type="password"
                      name="current_password"
                      value={passwordForm.current_password}
                      onChange={handlePasswordChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                      placeholder="••••••••"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <KeyRound className="w-4 h-4 text-amber-500" /> Nouveau mot de passe
                        </span>
                      </label>
                      <input
                        type="password"
                        name="new_password"
                        value={passwordForm.new_password}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                        placeholder="••••••••"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <span className="flex items-center gap-2">
                          <Check size={16} className="text-green-500" /> Confirmer le mot de passe
                        </span>
                      </label>
                      <input
                        type="password"
                        name="confirm_password"
                        value={passwordForm.confirm_password}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent transition"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                  
                  {/* Password strength indicator */}
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Lock size={16} /> Sécurité du mot de passe
                    </p>
                    <div className="space-y-1 text-sm">
                      <p className={`flex items-center gap-2 ${passwordForm.new_password.length >= 6 ? 'text-green-600' : 'text-gray-400'}`}>
                        {passwordForm.new_password.length >= 6 ? <Check size={14} /> : <Circle size={14} />} Au moins 6 caractères
                      </p>
                      <p className={`flex items-center gap-2 ${passwordForm.new_password === passwordForm.confirm_password && passwordForm.new_password ? 'text-green-600' : 'text-gray-400'}`}>
                        {passwordForm.new_password === passwordForm.confirm_password && passwordForm.new_password ? <Check size={14} /> : <Circle size={14} />} Les mots de passe correspondent
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-4 border-t border-gray-100">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          <span>Mise à jour...</span>
                        </>
                      ) : (
                        <>
                          <KeyRound size={18} />
                          <span>Mettre à jour le mot de passe</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Danger Tab */}
            {activeTab === 'danger' && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle size={20} className="text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">Gestion du compte</h2>
                    <p className="text-sm text-gray-500">Actions sur votre compte</p>
                  </div>
                </div>
                
                {loadingRequest ? (
                  <div className="text-center py-8">
                    <Loader2 size={28} className="animate-spin mx-auto text-gray-400" />
                    <p className="text-gray-500 mt-2">Chargement...</p>
                  </div>
                ) : deletionRequest && deletionRequest.statut !== 'refuse' ? (
                  /* Demande de suppression en cours ou approuvée */
                  <div className={`border-2 rounded-2xl p-6 ${
                    deletionRequest.statut === 'en_attente' 
                      ? 'border-amber-200 bg-amber-50/50' 
                      : 'border-green-200 bg-green-50/50'
                  }`}>
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        deletionRequest.statut === 'en_attente' ? 'bg-amber-100' : 'bg-green-100'
                      }`}>
                        {deletionRequest.statut === 'en_attente' 
                          ? <Clock size={24} className="text-amber-600" /> 
                          : <Check size={24} className="text-green-600" />}
                      </div>
                      <div className="flex-1">
                        <h3 className={`text-lg font-bold mb-2 ${
                          deletionRequest.statut === 'en_attente' ? 'text-amber-700' : 'text-green-700'
                        }`}>
                          {deletionRequest.statut === 'en_attente' 
                            ? 'Demande de suppression en attente' 
                            : 'Demande approuvée'}
                        </h3>
                        <p className={`text-sm mb-4 ${
                          deletionRequest.statut === 'en_attente' ? 'text-amber-600' : 'text-green-600'
                        }`}>
                          Votre demande a été envoyée le {new Date(deletionRequest.date_demande).toLocaleDateString('fr-FR', { 
                            day: 'numeric', month: 'long', year: 'numeric' 
                          })}
                        </p>
                        <div className={`bg-white rounded-xl p-4 mb-4 border ${
                          deletionRequest.statut === 'en_attente' ? 'border-amber-200' : 'border-green-200'
                        }`}>
                          <p className="text-sm text-gray-600 font-medium mb-1">Raison :</p>
                          <p className="text-gray-700">{deletionRequest.raison}</p>
                        </div>
                        <div className="flex items-center gap-2 mb-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${
                            deletionRequest.statut === 'en_attente' ? 'bg-amber-100 text-amber-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {deletionRequest.statut === 'en_attente' 
                              ? <><Clock size={14} /> En attente de traitement</> 
                              : <><Check size={14} /> Approuvée</>}
                          </span>
                        </div>
                        {deletionRequest.statut === 'en_attente' && (
                          <button
                            onClick={handleCancelDeletionRequest}
                            disabled={loading}
                            className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white font-semibold rounded-xl hover:bg-gray-700 transition-all"
                          >
                            <Undo2 size={18} />
                            <span>{loading ? 'Annulation...' : 'Annuler ma demande'}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Pas de demande en cours ou demande refusée - peut en faire une nouvelle */
                  <div className="space-y-4">
                    {/* Message si demande précédente refusée */}
                    {deletionRequest && deletionRequest.statut === 'refuse' && (
                      <div className="border-2 border-red-200 bg-red-50/50 rounded-2xl p-6 mb-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <XCircle size={24} className="text-red-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-red-700 mb-2">Demande précédente refusée</h3>
                            <p className="text-red-600 text-sm mb-2">
                              Votre demande du {new Date(deletionRequest.date_demande).toLocaleDateString('fr-FR', { 
                                day: 'numeric', month: 'long', year: 'numeric' 
                              })} a été refusée.
                            </p>
                            {deletionRequest.commentaire_admin && (
                              <div className="bg-white rounded-xl p-4 border border-red-200">
                                <p className="text-sm font-medium text-red-700 mb-1">Raison du refus :</p>
                                <p className="text-red-600">{deletionRequest.commentaire_admin}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Formulaire nouvelle demande */}
                    <div className="border-2 border-red-200 bg-red-50/50 rounded-2xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Trash2 size={24} className="text-red-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-red-700 mb-2">
                            {deletionRequest?.statut === 'refuse' 
                              ? 'Soumettre une nouvelle demande' 
                              : 'Demander la suppression de mon compte'}
                          </h3>
                          <p className="text-red-600 text-sm mb-4">
                            La suppression du compte est soumise à validation par un administrateur. Après approbation :
                          </p>
                          <ul className="text-sm text-red-600 space-y-1 mb-6">
                            <li className="flex items-center gap-2">
                              <span>•</span> Vos projets seront suspendus
                            </li>
                            <li className="flex items-center gap-2">
                              <span>•</span> Vos services et boutique seront désactivés
                            </li>
                            <li className="flex items-center gap-2">
                              <span>•</span> Votre abonnement sera annulé
                            </li>
                            <li className="flex items-center gap-2">
                              <span>•</span> Vous ne pourrez plus vous connecter
                            </li>
                          </ul>
                          <button
                            onClick={() => setShowDeleteModal(true)}
                            className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all"
                          >
                            <Trash2 size={18} />
                            <span>{deletionRequest?.statut === 'refuse' ? 'Nouvelle demande' : 'Demander la suppression'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/profile" className="bg-white rounded-xl shadow-md p-4 text-center hover:shadow-lg transition flex flex-col items-center">
            <User size={24} className="text-indigo-500 mb-1" />
            <span className="text-sm font-medium text-gray-700">Mon profil</span>
          </Link>
          <Link to="/my-applications" className="bg-white rounded-xl shadow-md p-4 text-center hover:shadow-lg transition flex flex-col items-center">
            <ClipboardList size={24} className="text-green-500 mb-1" />
            <span className="text-sm font-medium text-gray-700">Candidatures</span>
          </Link>
          <Link to="/notifications" className="bg-white rounded-xl shadow-md p-4 text-center hover:shadow-lg transition flex flex-col items-center">
            <Bell size={24} className="text-amber-500 mb-1" />
            <span className="text-sm font-medium text-gray-700">Notifications</span>
          </Link>
          <Link to="/" className="bg-white rounded-xl shadow-md p-4 text-center hover:shadow-lg transition flex flex-col items-center">
            <Home size={24} className="text-indigo-500 mb-1" />
            <span className="text-sm font-medium text-gray-700">Accueil</span>
          </Link>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Demande de suppression</h3>
              <p className="text-gray-600 text-sm">
                Veuillez nous indiquer la raison de votre demande. Un administrateur examinera votre demande.
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Pourquoi souhaitez-vous supprimer votre compte ?
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Ex: Je n'utilise plus la plateforme, j'ai trouvé une alternative..."
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-red-400 resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">{deleteReason.length}/500 caractères</p>
            </div>
            
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 mb-4">
              <p className="text-sm text-amber-700 flex items-start gap-2">
                <span>ℹ️</span>
                <span>Votre demande sera examinée sous 48h. Vous pourrez l'annuler tant qu'elle n'a pas été traitée.</span>
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteReason('');
                }}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={loading || !deleteReason.trim()}
                className="flex-1 px-4 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Envoi...' : 'Envoyer la demande'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
